import { defineStore } from 'pinia'
import jwtInterceptor from "@/plugins/jwtInterceptor";
import { PlannedTask } from '@/interfaces/api/tasks';
import * as Sentry from '@sentry/vue'
import type { EventFollowUp, EventAttachment } from '@/interfaces/api/plannedWorkEvent'
import type { PlannedWorkTaskAttachment } from '@/interfaces/api/plannedWorkAttachment'

export function getBestTime(item: PlannedTask): number {
  const f = item.Flight as any
  const t = f?.ActualInBlockTime ?? f?.EstimatedInBlockTime ?? f?.ScheduledInBlockTime ?? f?.ScheduleTime ?? item.Start
  if (!t) return Infinity

  const ms = new Date(t).getTime()

  // Night-shift tasks have Start = 00:00 UTC and no flight block times.
  // UTC midnight is the numerically smallest timestamp of any day, causing them
  // to sort before all daytime tasks. Push them to the 22:00 position instead.
  if (item.TypeOfWork === 'flight.night') {
    const d = new Date(t)
    if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0) {
      return ms + 22 * 60 * 60 * 1000
    }
  }

  return ms
}

export const usePlannedWorkStore = defineStore('plannedWork', {
    state: () :  {
        tasks: PlannedTask[],
        yourTasks: PlannedTask[],
        yourPastTasks: PlannedTask[],
        selectedTask?: PlannedTask,
        mercureToken: string | null,
        mercureTopic: string | null,
        eventFollowUps: Record<number, EventFollowUp[]>,
        eventAttachments: Record<number, EventAttachment[]>,
        // DEVAMS-2619: task-level (no-event) attachments — distinct from eventAttachments above.
        taskAttachments: Record<number, PlannedWorkTaskAttachment[]>,
        isLoadingFollowUps: Record<number, boolean>,
        isLoadingAttachments: Record<number, boolean>,
        // DEVAMS-2619: separate loading flag for task-level attachments so it doesn't
        // collide with isLoadingAttachments (used by the event-attachments fetch).
        isLoadingTaskAttachments: Record<number, boolean>,
    } => ({
        tasks: [],
        yourTasks: [],
        yourPastTasks: [],
        mercureToken: null,
        mercureTopic: null,
        eventFollowUps: {},
        eventAttachments: {},
        taskAttachments: {},
        isLoadingFollowUps: {},
        isLoadingAttachments: {},
        isLoadingTaskAttachments: {},
    }),
    getters: {
        getTasks(state){
            return state.tasks
        },
        getSelectedTask(state){
            return state.selectedTask
        },
        getMyTasks(state){
            return state.yourTasks;
        },
        getPastTasks(state){
            return state.yourPastTasks;
        }
    },
    actions: {
        renewTasks(silent = false) {
            if(!silent) {
                emitter.emit('loading', true);
            }
            return jwtInterceptor.get('v1/user-tasks/overview')
                .then(response => {
                        this.tasks = (response.data.plannedWork as PlannedTask[]).sort(
                            (a, b) => getBestTime(a) - getBestTime(b)
                        )
                        // Capture Mercure headers for SSE subscription (only on first call / token refresh)
                        const token = response.headers?.['x-mercure-token']
                        const topic = response.headers?.['x-mercure-topic']
                        if (token) this.mercureToken = token
                        if (topic) this.mercureTopic = topic
                    if(!silent) {
                        emitter.emit('loading', false);
                    }
                    }
                ). catch(error => {
                    if(!silent) {
                        emitter.emit('loading', false);
                    }
                    console.error(error);
            })
        },
        renewYourTasks(silent = false) {
            if (!silent) {
                emitter.emit('loading', true);
            }
            return Promise.allSettled([
                jwtInterceptor.get('v1/user-tasks'),
                jwtInterceptor.get('v1/user-tasks/past'),
            ]).then(([tasksResult, pastTasksResult]) => {
                if (tasksResult.status === 'fulfilled') {
                    this.yourTasks = (tasksResult.value.data.plannedWork as PlannedTask[])
                        .sort((a, b) => getBestTime(a) - getBestTime(b));
                } else {
                    console.error(tasksResult.reason);
                }
                if (pastTasksResult.status === 'fulfilled') {
                    this.yourPastTasks = pastTasksResult.value.data;
                } else {
                    console.error(pastTasksResult.reason);
                }
            }).finally(() => {
                if (!silent) {
                    emitter.emit('loading', false);
                }
            });
        },
        async loadTaskById(id: number) {
            try {
                const response = await jwtInterceptor.get('v1/plannedwork/task/' + id)
                this.selectedTask = response.data
                return response.data
            } catch (error: unknown) {
                type AxiosErrorLike = { response?: { status?: number } }
                const status = (error as AxiosErrorLike)?.response?.status
                if (status === 404) {
                    this.selectedTask = undefined
                    error._handledByComponent = true
                    const snackData: snackevent = {
                        snackbarText: `Task ${id} not found. Returning to overview.`,
                        snackIcon: 'mdi-alert-circle-outline',
                        snackColor: 'error',
                        error: error
                    }
                    // Global emitter already used elsewhere in this store
                    emitter.emit('snackbar', snackData)
                    return null
                }
                console.error(error)
                throw error
            }
        },
        addComment(plannedWorkID : string, commentString: string){
            const newComment = {
                Text: commentString,
                type: 'API',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            }
            if (this.selectedTask && this.selectedTask.Timeline) {
                this.selectedTask.Timeline.push(newComment)
            }

            return jwtInterceptor.post('v1/user-tasks/comment/'+plannedWorkID, {
                comment: commentString
            }).then(() => {
                // FIX: Wrapped in an arrow function
                // Also ensure type consistency (converting string to number if ID is numeric)
                return this.loadTaskById(Number(plannedWorkID))
            })
        },
        patchTaskFlight(flightId: number, data: Record<string, unknown>): void {
            for (const list of [this.yourTasks, this.yourPastTasks]) {
                if (!Array.isArray(list)) continue
                for (const task of list) {
                    if (task.Flight && (task.Flight as any).id === flightId) {
                        Object.assign(task.Flight, data)
                    }
                }
            }
        },
        async updateAcStatus(id: number, acStatus: 'serviceable' | 'critical' | 'aog'): Promise<void> {
            await jwtInterceptor.patch(`v1/plannedwork/task/${id}/ac-status`, { acStatus })
        },

        // ── Waiting for Parts (DEVAMS-2571) ───────────────────────────────────────

        async startWaitingForParts(id: number): Promise<void> {
            try {
                const response = await jwtInterceptor.post(`v1/plannedwork/task/${id}/waiting-for-parts/start`)
                if (this.selectedTask?.id === id) {
                    this.selectedTask.waitingForPartsPeriod = response?.data
                }
                // Reload so the Timeline (and everything else) reflects this action
                // immediately, same as every other task-mutating action in this store.
                await this.loadTaskById(id)
            } catch (error) {
                Sentry.captureException(error, { tags: { store: 'plannedWork', action: 'startWaitingForParts', taskId: id } })
                throw error
            }
        },

        async stopWaitingForParts(id: number): Promise<void> {
            try {
                const response = await jwtInterceptor.post(`v1/plannedwork/task/${id}/waiting-for-parts/stop`)
                if (this.selectedTask?.id === id) {
                    const closedPeriod = response?.data
                    this.selectedTask.waitingForPartsPeriod = closedPeriod
                    // DEVAMS-2596: optimistically fold in the just-closed period's duration
                    // so the counter freezes correctly right away, without waiting on the
                    // reload below to round-trip.
                    if (closedPeriod?.until) {
                        const elapsedSeconds = Math.max(
                            0,
                            Math.round((new Date(closedPeriod.until).getTime() - new Date(closedPeriod.from).getTime()) / 1000),
                        )
                        this.selectedTask.waitingForPartsTotalSeconds = (this.selectedTask.waitingForPartsTotalSeconds ?? 0) + elapsedSeconds
                    }
                }
                // Reload so the Timeline (and everything else) reflects this action
                // immediately, same as every other task-mutating action in this store.
                await this.loadTaskById(id)
            } catch (error) {
                Sentry.captureException(error, { tags: { store: 'plannedWork', action: 'stopWaitingForParts', taskId: id } })
                throw error
            }
        },

        // ── Event Tracking (DEVAMS-2072) ──────────────────────────────────────────

        async updateTaskTitle(id: number, title: string): Promise<void> {
            try {
                await jwtInterceptor.patch(`v1/plannedwork/task/${id}/title`, { title })
                await this.loadTaskById(id)
            } catch (error) {
                Sentry.captureException(error, { tags: { store: 'plannedWork', action: 'updateTaskTitle', taskId: id } })
                throw error
            }
        },

        async updateEventDescription(id: number, eventDescription: string | null): Promise<void> {
            try {
                await jwtInterceptor.patch(`v1/plannedwork/task/${id}/event-description`, { eventDescription })
                await this.loadTaskById(id)
            } catch (error) {
                Sentry.captureException(error, { tags: { store: 'plannedWork', action: 'updateEventDescription', taskId: id } })
                throw error
            }
        },

        async closeEvent(id: number, closed: boolean): Promise<void> {
            try {
                await jwtInterceptor.patch(`v1/plannedwork/task/${id}/event-closed`, { closed })
                // Reload the full task so the new timeline entry appears immediately.
                await this.loadTaskById(id)
            } catch (error) {
                Sentry.captureException(error, { tags: { store: 'plannedWork', action: 'closeEvent', taskId: id } })
                throw error
            }
        },

        async fetchEventFollowUps(id: number): Promise<void> {
            this.isLoadingFollowUps = { ...this.isLoadingFollowUps, [id]: true }
            try {
                const response = await jwtInterceptor.get(`v1/plannedwork/task/${id}/event-followups`)
                this.eventFollowUps = { ...this.eventFollowUps, [id]: response?.data ?? [] }
            } catch (error) {
                Sentry.captureException(error, { tags: { store: 'plannedWork', action: 'fetchEventFollowUps', taskId: id } })
                throw error
            } finally {
                this.isLoadingFollowUps = { ...this.isLoadingFollowUps, [id]: false }
            }
        },

        async addEventFollowUp(id: number, content: string): Promise<void> {
            try {
                await jwtInterceptor.post(`v1/plannedwork/task/${id}/event-followups`, { content })
                await this.fetchEventFollowUps(id)
            } catch (error) {
                Sentry.captureException(error, { tags: { store: 'plannedWork', action: 'addEventFollowUp', taskId: id } })
                throw error
            }
        },

        async updateEventFollowUp(taskId: number, followUpId: number, content: string): Promise<void> {
            try {
                await jwtInterceptor.patch(`v1/plannedwork/task/${taskId}/event-followups/${followUpId}`, { content })
                await this.fetchEventFollowUps(taskId)
            } catch (error) {
                Sentry.captureException(error, { tags: { store: 'plannedWork', action: 'updateEventFollowUp', taskId, followUpId } })
                throw error
            }
        },

        async fetchEventAttachments(id: number): Promise<void> {
            this.isLoadingAttachments = { ...this.isLoadingAttachments, [id]: true }
            try {
                const response = await jwtInterceptor.get(`v1/plannedwork/task/${id}/event-attachments`)
                this.eventAttachments = { ...this.eventAttachments, [id]: response?.data ?? [] }
            } catch (error) {
                Sentry.captureException(error, { tags: { store: 'plannedWork', action: 'fetchEventAttachments', taskId: id } })
                throw error
            } finally {
                this.isLoadingAttachments = { ...this.isLoadingAttachments, [id]: false }
            }
        },

        async uploadEventAttachment(id: number, file: File): Promise<void> {
            try {
                const formData = new FormData()
                formData.append('file', file)
                await jwtInterceptor.post(`v1/plannedwork/task/${id}/event-attachments`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                await this.fetchEventAttachments(id)
            } catch (error) {
                Sentry.captureException(error, { tags: { store: 'plannedWork', action: 'uploadEventAttachment', taskId: id } })
                throw error
            }
        },

        async deleteEventAttachment(taskId: number, attachmentId: number, source: 'task' | 'event' = 'task'): Promise<void> {
            try {
                const url = source === 'event'
                    ? `v1/plannedwork/task/${taskId}/event-attachments/${attachmentId}?source=event`
                    : `v1/plannedwork/task/${taskId}/event-attachments/${attachmentId}`
                await jwtInterceptor.delete(url)
                if (this.eventAttachments[taskId]) {
                    this.eventAttachments = {
                        ...this.eventAttachments,
                        [taskId]: this.eventAttachments[taskId].filter((a) => a.id !== attachmentId),
                    }
                }
            } catch (error) {
                Sentry.captureException(error, { tags: { store: 'plannedWork', action: 'deleteEventAttachment', taskId, attachmentId } })
                throw error
            }
        },
        // ── Task Attachments (DEVAMS-2156) ────────────────────────────────────────

        /**
         * Uploads a file directly on a task (not the workpack sign-off attachment).
         * POST /api/v1/plannedwork/task/{id}/attachments
         * Returns the created attachment record.
         */
        async uploadTaskAttachment(taskId: number, file: File): Promise<PlannedWorkTaskAttachment> {
            try {
                const formData = new FormData()
                formData.append('file', file)
                const response = await jwtInterceptor.post<PlannedWorkTaskAttachment>(
                    `v1/plannedwork/task/${taskId}/attachments`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } },
                )
                // DEVAMS-2619: refresh the list so the newly uploaded attachment is rendered —
                // mirrors uploadEventAttachment's fetchEventAttachments call above.
                await this.fetchTaskAttachments(taskId)
                return response.data
            } catch (error) {
                Sentry.captureException(error, {
                    tags: { store: 'plannedWork', action: 'uploadTaskAttachment', taskId },
                })
                throw error
            }
        },

        /**
         * DEVAMS-2619: Fetches task-level attachments (uploaded via uploadTaskAttachment).
         * GET /api/v1/plannedwork/task/{id}/attachments
         */
        async fetchTaskAttachments(taskId: number): Promise<void> {
            this.isLoadingTaskAttachments = { ...this.isLoadingTaskAttachments, [taskId]: true }
            try {
                const response = await jwtInterceptor.get(`v1/plannedwork/task/${taskId}/attachments`)
                this.taskAttachments = { ...this.taskAttachments, [taskId]: response?.data ?? [] }
            } catch (error) {
                Sentry.captureException(error, { tags: { store: 'plannedWork', action: 'fetchTaskAttachments', taskId } })
                throw error
            } finally {
                this.isLoadingTaskAttachments = { ...this.isLoadingTaskAttachments, [taskId]: false }
            }
        },

        /**
         * DEVAMS-2619: Deletes a task-level attachment.
         * DELETE /api/v1/plannedwork/task/{id}/attachments/{attachmentId}
         */
        async deleteTaskAttachment(taskId: number, attachmentId: number): Promise<void> {
            try {
                await jwtInterceptor.delete(`v1/plannedwork/task/${taskId}/attachments/${attachmentId}`)
                if (this.taskAttachments[taskId]) {
                    this.taskAttachments = {
                        ...this.taskAttachments,
                        [taskId]: this.taskAttachments[taskId].filter((a) => a.id !== attachmentId),
                    }
                }
            } catch (error) {
                Sentry.captureException(error, { tags: { store: 'plannedWork', action: 'deleteTaskAttachment', taskId, attachmentId } })
                throw error
            }
        },

        // state = Unconfirmed, Confirmed, Start, Left or Finished,
        updateAttendance(plannedWorkID: string, newState: string){
           return jwtInterceptor.post('v1/user-tasks/state/'+plannedWorkID, {
               state: newState
           }).then(() => {
                return true;
               }
           ).catch(error => {
               console.error(error);
               return error;
           })
        },
    },
})
